/**
 * Card 1 "Book It" send-off — the reservation card stays in place while a field
 * of cyan particles flies in from the outer edges, settles over the card, then
 * gets swept up in a left→right gust and blown off the right edge. Plays once
 * after the populate build-up (see ScheduleAnimation); the card then rests on
 * the created state. Transform/opacity only; honors prefers-reduced-motion.
 *
 * Percentages below are of the send-off animation itself (it runs with its own
 * duration + delay), not the section cycle.
 */
const CYAN = "#44C2E3";
const EASE_ENERGETIC = "cubic-bezier(0.7, 0, 0.84, 0)";
const EASE_CALM = "cubic-bezier(0.65, 0, 0.35, 1)";
const PARTICLE_COUNT = 34;

// Deterministic pseudo-random (stable; no RNG).
const hash = (i: number, salt: number): number => {
  const x = Math.sin(i * 12.9898 + salt * 78.233) * 43758.5453;
  return x - Math.floor(x);
};

// Entry point just off the card's nearest edge, radially outward from center
// (cqw, width-relative; the 4:3 card is 100 wide × 75 tall in those units).
const entryOffset = (sx: number, sy: number) => {
  const px = sx;
  const py = sy * 0.75;
  let dx = px - 50;
  let dy = py - 37.5;
  let len = Math.hypot(dx, dy);
  if (len < 1) {
    dx = 0;
    dy = -1;
    len = 1;
  }
  const ux = dx / len;
  const uy = dy / len;
  const tx = ux > 0 ? (100 - px) / ux : ux < 0 ? -px / ux : Infinity;
  const ty = uy > 0 ? (75 - py) / uy : uy < 0 ? -py / uy : Infinity;
  const dist = Math.min(tx, ty) + 18;
  return { ox: +(ux * dist).toFixed(1), oy: +(uy * dist).toFixed(1) };
};

export interface Particle {
  sx: number;
  sy: number;
  size: number;
}

const SWIRL = Array.from({ length: PARTICLE_COUNT }, (_, i) => {
  const sx = +(12 + hash(i, 1) * 76).toFixed(2);
  const sy = +(14 + hash(i, 2) * 72).toFixed(2);
  const endX = +(120 + hash(i, 4) * 80).toFixed(1);
  const endY = +(-(26 + hash(i, 5) * 68)).toFixed(1);
  return {
    sx,
    sy,
    size: +(3 + hash(i, 3) * 4).toFixed(1),
    midX: +(28 + hash(i, 6) * 40).toFixed(1),
    midY: +((hash(i, 7) < 0.16 ? 1 : -1) * (12 + hash(i, 7) * 40)).toFixed(1),
    endX,
    endY,
    angle: Math.round((Math.atan2(endY, endX) * 180) / Math.PI),
    ...entryOffset(sx, sy),
  };
});

/** Render data for the particle spans (positions + size). */
export const PARTICLES: Particle[] = SWIRL.map((p) => ({ sx: p.sx, sy: p.sy, size: p.size }));

/**
 * Keyframes + per-particle classes for the one-shot send-off. The animation runs
 * for `sendMs` after `delayMs` (the build-up), holding the "blown off" end state.
 */
export const buildParticleCss = (sendMs: number, delayMs: number): string => {
  const frames = SWIRL.map((p, i) => {
    const enter = `translate(calc(-50% + ${p.ox}cqw), calc(-50% + ${p.oy}cqw)) scale(0.6)`;
    const arrive = +(18 + hash(i, 11) * 22).toFixed(2);
    const stag = (p.sx / 100) * 0.8 + hash(i, 9) * 0.2;
    const sweepStart = +(46 + stag * 22).toFixed(2);
    const mid = +(sweepStart + (100 - sweepStart) * 0.5).toFixed(2);
    return `
@keyframes hiw-sched-particle-${i} {
  0% { opacity: 0; transform: ${enter}; animation-timing-function: ${EASE_CALM}; }
  ${arrive}% { opacity: 0.95; transform: translate(-50%, -50%) scale(1); animation-timing-function: ${EASE_CALM}; }
  ${sweepStart}% { opacity: 0.95; transform: translate(-50%, -50%) scale(1); animation-timing-function: ${EASE_ENERGETIC}; }
  ${mid}% { opacity: 0.92; transform: translate(calc(-50% + ${p.midX}cqw), calc(-50% + ${p.midY}cqw)) rotate(${Math.round(p.angle * 0.7)}deg) scale(1.5, 0.8); }
  100% { opacity: 0; transform: translate(calc(-50% + ${p.endX}cqw), calc(-50% + ${p.endY}cqw)) rotate(${p.angle}deg) scale(2.4, 0.45); }
}
.hiw-sched-particle-${i} { animation: hiw-sched-particle-${i} ${sendMs}ms linear ${delayMs}ms forwards; }`;
  }).join("\n");

  return `
${frames}
/* Particles are hidden until their send-off animation starts, and in reduced motion. */
.hiw-sched-particle { opacity: 0; will-change: transform, opacity; }
@media (prefers-reduced-motion: reduce) {
  .hiw-sched-particle { animation: none !important; opacity: 0 !important; transform: none !important; }
}
`;
};

/** Inline style/colour for a particle span. */
export const PARTICLE_BG = CYAN;
export const PARTICLE_SHADOW = "0 0 6px rgba(68,194,227,0.7)";
